import { useState, useEffect } from 'react';
import { Box, Accordion, useDisclosure } from '@chakra-ui/react';

import { RiskTypeCard, InsuredCard } from './location';
import CollapsibleItemCard from '../components/CollapsibleItemCard';
import AddItemButton from '../components/AddItemButton';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import { useAnswerCodes, useLockAwareAccordion } from '../hooks';
import type { Location, LocationsData } from 'api';
import {
  createEmptyLocation,
  formatAddress,
  getRiskAddress,
} from '../constants/locationOptions';

interface LocationStepProps {
  data: LocationsData;
  quoteId: number;
  onUpdate: (locations: Location[]) => void;
  isUpdating?: boolean;
  customerType?: string;
}

const LocationStep = ({
  data,
  quoteId,
  onUpdate,
  isUpdating,
  customerType,
}: LocationStepProps) => {
  const { getLabel } = useAnswerCodes();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const [locationToDelete, setLocationToDelete] = useState<number | null>(null);
  const [locations, setLocations] = useState<Location[]>(data.locations || []);
  const [expandedIndex, setExpandedIndex] = useState<number>(0);
  const accordionProps = useLockAwareAccordion({
    itemCount: locations.length,
    expandedIndex,
    setExpandedIndex,
  });

  useEffect(() => {
    if (data.locations && data.locations.length > 0) {
      setLocations(data.locations);
    }
  }, [data.locations]);

  const handleLocationChange = (
    index: number,
    field: string | Record<string, any>,
    value?: any,
    nestedField?: string,
  ) => {
    const updated = [...locations];

    if (typeof field === 'object') {
      updated[index] = { ...updated[index], ...field };
    } else if (nestedField) {
      const currentFieldValue = updated[index][field as keyof Location];
      const nestedObject =
        typeof currentFieldValue === 'object' && currentFieldValue !== null
          ? currentFieldValue
          : {};
      updated[index] = {
        ...updated[index],
        [field]: {
          ...nestedObject,
          [nestedField]: value,
        },
      };
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }

    setLocations(updated);
    onUpdate(updated);
  };

  const handleAddLocation = () => {
    const maxId = Math.max(...locations.map((l) => l.id), 0);
    const newLocation = createEmptyLocation(maxId + 1, data.province);
    const updated = [...locations, newLocation];
    setLocations(updated);
    setExpandedIndex(updated.length - 1);
    onUpdate(updated);
  };

  const handleDeleteClick = (index: number) => {
    setLocationToDelete(index);
    onDeleteOpen();
  };

  const handleConfirmDelete = () => {
    if (locationToDelete === null) return;

    const updated = locations.filter((_, i) => i !== locationToDelete);
    setLocations(updated);
    if (updated.length === 0) {
      setExpandedIndex(-1);
    } else if (expandedIndex >= updated.length) {
      setExpandedIndex(updated.length - 1);
    }
    onUpdate(updated);
    onDeleteClose();
    setLocationToDelete(null);
  };

  const getLocationTitle = (location: Location) => {
    const riskType = location.location_type
      ? getLabel(location.location_type, 'location_type')
      : '';
    const fullAddress = formatAddress(getRiskAddress(location));

    if (riskType && fullAddress) {
      return `${riskType} – ${fullAddress}`;
    }
    return riskType || fullAddress || 'New Location';
  };

  return (
    <Box>
      <Accordion {...accordionProps}>
        {locations.map((location, index) => (
          <CollapsibleItemCard
            key={location.id}
            title={getLocationTitle(location)}
            onDelete={() => handleDeleteClick(index)}
          >
            <RiskTypeCard
              location={location}
              index={index}
              isUpdating={isUpdating}
              onLocationChange={handleLocationChange}
              quoteId={quoteId}
            />
            <InsuredCard
              location={location}
              index={index}
              isUpdating={isUpdating}
              onLocationChange={handleLocationChange}
              customerType={customerType}
            />
          </CollapsibleItemCard>
        ))}
      </Accordion>

      <AddItemButton label="Location" onClick={handleAddLocation} />

      <DeleteConfirmDialog
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        onConfirm={handleConfirmDelete}
        entityName="Location"
        itemTitle={
          locationToDelete !== null
            ? getLocationTitle(locations[locationToDelete])
            : 'this location'
        }
      />
    </Box>
  );
};

export default LocationStep;
